import { useState, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useStore } from '@/hooks/useStore';
import {
  getLogsByFarmer, getFieldsByFarmer, getSeasonsByFarmer, getMaterialsByFarmer,
  addLog, updateLog, softDeleteLog, addEditHistory, getEditHistories,
} from '@/store';
import { showToast } from '@/components/base/Toast';
import { EmptyState } from '@/components/base/EmptyState';
import { getAllWarnings, getGlobalWarningLevel, getFertilizerMissingWarning, WARNING_LEVELS, WARNING_TAG_CONFIG } from '@/utils/logParser';
import { LogFilters } from './components/LogFilters';
import { LogCard } from './components/LogCard';
import { LogFormModal } from './components/LogFormModal';
import { LogDetailModal } from './components/LogDetailModal';
import { LogDeleteModal } from './components/LogDeleteModal';
import { QuickDiarySheet } from '@/pages/farmer/info/components/QuickDiarySheet';
import type { Diary } from '@/types';

export default function FarmerLogPage() {
  useStore();
  const { user } = useAuth();
  const farmerId = user?.farmerId || 'f1';

  const allLogs = getLogsByFarmer(farmerId);
  const fields = getFieldsByFarmer(farmerId);
  const seasons = getSeasonsByFarmer(farmerId);
  const farmerMaterials = getMaterialsByFarmer(farmerId);

  const [search, setSearch] = useState('');
  const [filterField, setFilterField] = useState('');
  const [filterSeason, setFilterSeason] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const [showHTXNote, setShowHTXNote] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState<Diary | null>(null);
  const [detailItem, setDetailItem] = useState<Diary | null>(null);
  const [deleteItem, setDeleteItem] = useState<Diary | null>(null);

  // Quick entry state
  const [quickEntry, setQuickEntry] = useState('');
  const [quickFieldId, setQuickFieldId] = useState(fields[0]?.id || '');
  const [quickSeasonId, setQuickSeasonId] = useState(seasons[0]?.id || '');
  const [showQuickForm, setShowQuickForm] = useState(false);


  // ===== WARNING SYSTEM =====
  const activeLogs = useMemo(() => allLogs.filter((l) => !l.isDeleted), [allLogs]);

  // 1. Gap warning — never having logged yet (a brand-new field/account) is not
  // the same as being overdue on an established habit, so only compute this once
  // there is at least one real entry to measure a gap from.
  const latestLogDate = activeLogs.length > 0
    ? activeLogs.reduce((max, l) => (l.ngay > max ? l.ngay : max), activeLogs[0].ngay)
    : null;
  // A farmer who has never written a diary is not 999 days late. If a season is
  // already under way the nudge is counted from its start date — that is a real
  // gap; with no season at all there is nothing to warn about yet.
  const cultivatingStart = seasons
    .filter((s) => s.status === 'Đang canh tác' || s.status === 'Sắp thu hoạch')
    .map((s) => s.startDate)
    .filter(Boolean)
    .sort()[0];
  const gapFrom = latestLogDate ?? cultivatingStart ?? null;
  const daysSinceLastLog = gapFrom
    ? Math.max(0, Math.floor((Date.now() - new Date(gapFrom).getTime()) / (1000 * 60 * 60 * 24)))
    : null;
  const globalWarning = getGlobalWarningLevel(daysSinceLastLog, activeLogs);

  // 2. Fertilizer missing warning
  const fertilizerWarning = getFertilizerMissingWarning(activeLogs, seasons);

  // 3. Active logs with warnings
  const logsWithWarnings = useMemo(() => {
    return activeLogs
      .map((log) => {
        const season = seasons.find((s) => s.id === log.seasonId);
        const { level, tags } = getAllWarnings(log, activeLogs, season);
        return { ...log, warningLevel: level, warningTags: tags };
      });
  }, [activeLogs, seasons]);

  // 4. Aggregate warning tags
  const activeWarningTags = useMemo(() => {
    const tagSet = new Set<string>();
    logsWithWarnings.forEach((l) => l.warningTags?.forEach((t) => tagSet.add(t)));
    return Array.from(tagSet);
  }, [logsWithWarnings]);

  // 5. Stats summary
  const warningCounts = useMemo(() => ({
    danger: logsWithWarnings.filter((l) => l.warningLevel === 'danger').length,
    check: logsWithWarnings.filter((l) => l.warningLevel === 'check').length,
    caution: logsWithWarnings.filter((l) => l.warningLevel === 'caution').length,
    total: activeLogs.length,
  }), [logsWithWarnings, activeLogs]);

  // ===== FILTERING =====
  const filteredLogs = useMemo(() => {
    let list = showDeleted ? allLogs.map((l) => {
      const existingWarned = logsWithWarnings.find((w) => w.id === l.id);
      return existingWarned || l;
    }) : logsWithWarnings;

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((l) =>
        l.hoatDong.toLowerCase().includes(q) ||
        l.chiTiet.toLowerCase().includes(q) ||
        (l.materialsNote || '').toLowerCase().includes(q) ||
        (l.ghiChuKhac || '').toLowerCase().includes(q)
      );
    }
    if (filterField) list = list.filter((l) => l.fieldId === filterField);
    if (filterSeason) list = list.filter((l) => l.seasonId === filterSeason);
    if (dateFrom) list = list.filter((l) => l.ngay >= dateFrom);
    if (dateTo) list = list.filter((l) => l.ngay <= dateTo);
    if (showWarning) list = list.filter((l) => !!l.warningLevel && l.warningLevel !== 'none');
    if (showHTXNote) list = list.filter((l) => !!l.ghiChuHTX);

    return list.sort((a, b) => (b.ngay > a.ngay ? 1 : -1));
  }, [allLogs, logsWithWarnings, showDeleted, search, filterField, filterSeason, dateFrom, dateTo, showWarning, showHTXNote]);

  const openCreate = () => {
    setEditItem(null);
    setFormOpen(true);
  };

  const openEdit = (log: Diary) => {
    setEditItem(log);
    setFormOpen(true);
  };

  const handleSave = (data: Omit<Diary, 'id'>) => {
    addLog({ ...data, farmerId, editCount: 0 });
    showToast('Tạo nhật ký thành công', 'success');
  };

  const handleUpdate = (id: string, data: Partial<Diary>) => {
    const oldLog = allLogs.find((l) => l.id === id);
    if (!oldLog) return;
    const editor = user?.displayName || 'Nông hộ';
    const now = new Date().toISOString();

    if (data.chiTiet !== undefined && data.chiTiet !== oldLog.chiTiet) {
      addEditHistory({ entityType: 'Diary', entityId: id, farmerId, truong: 'chiTiet', giaTriCu: oldLog.chiTiet, giaTriMoi: data.chiTiet, ngaySua: now, nguoiSua: editor });
    }
    if (data.ngay !== undefined && data.ngay !== oldLog.ngay) {
      addEditHistory({ entityType: 'Diary', entityId: id, farmerId, truong: 'ngay', giaTriCu: oldLog.ngay, giaTriMoi: data.ngay, ngaySua: now, nguoiSua: editor });
    }
    if (data.materialsNote !== undefined && data.materialsNote !== oldLog.materialsNote) {
      addEditHistory({ entityType: 'Diary', entityId: id, farmerId, truong: 'materialsNote', giaTriCu: oldLog.materialsNote || '', giaTriMoi: data.materialsNote || '', ngaySua: now, nguoiSua: editor });
    }

    updateLog(id, data, farmerId);
    showToast('Cập nhật nhật ký thành công', 'success');
  };

  const handleDeleteConfirm = (reason: string) => {
    if (!deleteItem) return;
    softDeleteLog(deleteItem.id, reason, farmerId);
    showToast('Đã xóa nhật ký', 'success');
    setDeleteItem(null);
  };

  // Quick entry submit
  const handleQuickSubmit = () => {
    if (!quickEntry.trim()) {
      showToast('Vui lòng nhập nội dung nhật ký', 'warning');
      return;
    }
    if (!quickSeasonId) {
      showToast('Vui lòng chọn mùa vụ', 'warning');
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    addLog({
      farmerId,
      fieldId: quickFieldId || undefined,
      seasonId: quickSeasonId,
      ngay: today,
      hoatDong: 'Cập nhật nhanh',
      chiTiet: quickEntry.trim(),
      materialsNote: undefined,
      editCount: 0,
    });
    showToast('Đã thêm nhật ký nhanh', 'success');
    setQuickEntry('');
    setShowQuickForm(false);
  };

  return (
    <div className="pb-4 max-w-[800px] mx-auto">
      <div className="mx-4 mt-4 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">Nhật ký canh tác</h2>
            <p className="text-xs text-slate-500 mt-0.5">{warningCounts.total} nhật ký · {warningCounts.danger + warningCounts.check + warningCounts.caution} cảnh báo</p>
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="px-3 py-2 rounded-xl text-xs font-bold bg-agrida-600 hover:bg-agrida-700 text-white transition-colors whitespace-nowrap min-h-[44px] flex items-center gap-1"
          >
            <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-add-line" /></span>
            Tạo nhật ký
          </button>
        </div>

        {/* Quick Entry Section */}
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <button
            type="button"
            onClick={() => setShowQuickForm(!showQuickForm)}
            className="w-full flex items-center justify-between px-4 py-3 text-left"
          >
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-xl bg-agrida-100 text-agrida-600 flex items-center justify-center">
                <i className="ri-flashlight-line" />
              </span>
              <span className="text-sm font-medium text-slate-800">Cập nhật nhanh</span>
            </div>
            <span className={`w-5 h-5 flex items-center justify-center text-slate-400 transition-transform ${showQuickForm ? 'rotate-180' : ''}`}>
              <i className="ri-arrow-down-s-line" />
            </span>
          </button>

          {showQuickForm && (
            <div className="px-4 pb-4 space-y-3 animate-slide-down">
              <div className="flex gap-2">
                <select
                  value={quickFieldId}
                  onChange={(e) => setQuickFieldId(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-agrida-500"
                >
                  <option value="">Chọn thửa đất</option>
                  {fields.map((f) => (<option key={f.id} value={f.id}>{f.name}</option>))}
                </select>
                <select
                  value={quickSeasonId}
                  onChange={(e) => setQuickSeasonId(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-agrida-500"
                >
                  <option value="">Chọn mùa vụ</option>
                  {seasons.filter((s) => !quickFieldId || s.fieldId === quickFieldId).map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </div>

              <textarea
                value={quickEntry}
                onChange={(e) => setQuickEntry(e.target.value)}
                placeholder="Hôm nay bạn đã làm gì trên đồng? (VD: Bón phân, phun thuốc, tưới nước...)"
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500 resize-none"
              />

              {/* Feedback 08 A4: this panel used to have "Ghi âm (mock)"/"Chụp
                  ảnh (mock)" buttons that did nothing — real capture (upload,
                  playback) already exists in "Tạo nhật ký đầy đủ" below, so
                  point there instead of half-building a second copy of it here. */}
              <button
                type="button"
                onClick={() => { setShowQuickForm(false); openCreate(); }}
                className="flex items-center gap-1.5 text-xs font-medium text-agrida-600"
              >
                <i className="ri-camera-line" />
                Cần đính kèm ảnh, video hoặc ghi âm? Dùng "Tạo nhật ký đầy đủ"
              </button>

              <div className="flex gap-2">
                <button type="button" onClick={() => { setShowQuickForm(false); setQuickEntry(''); }}
                  className="flex-1 px-4 py-2.5 rounded-xl text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[40px]">
                  Đóng
                </button>
                <button type="button" onClick={handleQuickSubmit}
                  className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold bg-agrida-600 hover:bg-agrida-700 text-white transition-colors whitespace-nowrap min-h-[40px]">
                  Lưu nhanh
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===== GLOBAL WARNINGS ===== */}
        {/* Gap warning */}
        {globalWarning && (
          <div className={`rounded-xl p-3 flex items-start gap-2 ${
            globalWarning.level === 'danger' ? 'bg-red-50 border border-red-200' :
            globalWarning.level === 'check' ? 'bg-orange-50 border border-orange-200' :
            'bg-amber-50 border border-amber-200'
          }`}>
            <span className={`w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 ${
              globalWarning.level === 'danger' ? 'text-red-600' :
              globalWarning.level === 'check' ? 'text-orange-600' :
              'text-amber-600'
            }`}>
              <i className={globalWarning.level === 'danger' ? 'ri-error-warning-fill' : globalWarning.level === 'check' ? 'ri-error-warning-line' : 'ri-alert-line'} />
            </span>
            <div>
              <p className={`text-xs font-medium ${
                globalWarning.level === 'danger' ? 'text-red-800' :
                globalWarning.level === 'check' ? 'text-orange-800' :
                'text-amber-800'
              }`}>{WARNING_LEVELS[globalWarning.level].label}</p>
              <p className={`text-[11px] ${
                globalWarning.level === 'danger' ? 'text-red-600' :
                globalWarning.level === 'check' ? 'text-orange-600' :
                'text-amber-600'
              }`}>{globalWarning.message}</p>
            </div>
          </div>
        )}

        {/* Fertilizer missing warning */}
        {fertilizerWarning && (
          <div className="rounded-xl p-3 flex items-start gap-2 bg-orange-50 border border-orange-200">
            <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 text-orange-600"><i className="ri-error-warning-line" /></span>
            <div>
              <p className="text-xs font-medium text-orange-800">Cần kiểm tra</p>
              <p className="text-[11px] text-orange-600">{fertilizerWarning.message}</p>
            </div>
          </div>
        )}

        {/* Warning tag chips */}
        {activeWarningTags.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {activeWarningTags.map((tag) => {
              const cfg = WARNING_TAG_CONFIG[tag];
              if (!cfg) return null;
              return (
                <div key={tag} className={`rounded-xl px-3 py-1.5 flex items-center gap-1.5 ${
                  cfg.level === 'danger' ? 'bg-red-50 border border-red-200' :
                  cfg.level === 'check' ? 'bg-orange-50 border border-orange-200' :
                  'bg-amber-50 border border-amber-200'
                }`}>
                  <span className={`w-4 h-4 flex items-center justify-center ${
                    cfg.level === 'danger' ? 'text-red-600' :
                    cfg.level === 'check' ? 'text-orange-600' :
                    'text-amber-600'
                  }`}><i className={`${cfg.icon} text-xs`} /></span>
                  <span className={`text-[10px] font-medium ${
                    cfg.level === 'danger' ? 'text-red-700' :
                    cfg.level === 'check' ? 'text-orange-700' :
                    'text-amber-700'
                  }`}>{cfg.label}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Stats summary cards */}
        {warningCounts.total > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {warningCounts.danger > 0 && (
              <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-red-700">{warningCounts.danger}</p>
                <p className="text-[10px] text-red-600 font-medium">Rủi ro cao</p>
              </div>
            )}
            {warningCounts.check > 0 && (
              <div className="bg-orange-50 border border-orange-100 rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-orange-700">{warningCounts.check}</p>
                <p className="text-[10px] text-orange-600 font-medium">Cần kiểm tra</p>
              </div>
            )}
            {warningCounts.caution > 0 && (
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-amber-700">{warningCounts.caution}</p>
                <p className="text-[10px] text-amber-600 font-medium">Cần chú ý</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="mx-4 mt-3">
        <LogFilters
          search={search} onSearchChange={setSearch}
          fieldId={filterField} onFieldChange={setFilterField}
          seasonId={filterSeason} onSeasonChange={setFilterSeason}
          dateFrom={dateFrom} onDateFromChange={setDateFrom}
          dateTo={dateTo} onDateToChange={setDateTo}
          showWarning={showWarning} onShowWarningChange={setShowWarning}
          showHTXNote={showHTXNote} onShowHTXNoteChange={setShowHTXNote}
          showDeleted={showDeleted} onShowDeletedChange={setShowDeleted}
          fields={fields} seasons={seasons}
        />
      </div>

      {/* Log list */}
      <div className="mx-4 mt-3">
        {filteredLogs.length === 0 ? (
          <EmptyState
            icon="ri-file-list-line"
            title="Chưa có nhật ký"
            description="Hãy thêm nhật ký sản xuất đầu tiên của bạn."
          />
        ) : (
          <div className="space-y-2">
            {filteredLogs.map((log) => (
              <LogCard
                key={log.id}
                log={log}
                field={fields.find((f) => f.id === log.fieldId)}
                season={seasons.find((s) => s.id === log.seasonId)}
                onView={() => setDetailItem(log)}
                onEdit={() => openEdit(log)}
                onDelete={() => setDeleteItem(log)}
              />
            ))}
          </div>
        )}
      </div>

      {formOpen && !editItem && (
        <QuickDiarySheet
          open
          farmerId={farmerId}
          onClose={() => setFormOpen(false)}
        />
      )}

      {formOpen && editItem && (
        <LogFormModal
          editItem={editItem}
          fields={fields}
          seasons={seasons}
          farmerId={farmerId}
          farmerMaterials={farmerMaterials}
          onSave={handleSave}
          onUpdate={handleUpdate}
          onClose={() => setFormOpen(false)}
        />
      )}

      {detailItem && (
        <LogDetailModal
          log={detailItem}
          field={fields.find((f) => f.id === detailItem.fieldId)}
          season={seasons.find((s) => s.id === detailItem.seasonId)}
          histories={getEditHistories().filter((h) => h.entityId === detailItem.id && h.entityType === 'Diary')}
          onClose={() => setDetailItem(null)}
        />
      )}

      {deleteItem && (
        <LogDeleteModal
          logName={deleteItem.hoatDong}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteItem(null)}
        />
      )}
    </div>
  );
}
